import _ from 'lodash';
import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {WorkspaceSectionTypesEnum, WorkspaceTagV2} from '../../../models/api/workspaceModel';
import {InboxesFiltersEnum} from '../../../models/url/inboxesUrlModel';
import {AppState} from '../../appState';
import {getVisibleDelegations} from '../delegations/delegationsSelectors';
import {
  getDisplayedTeammateIds,
  getShouldAlphabetizeWorkspace,
  getShouldUseOpenFilterForTeams,
} from '../preferences/preferencesSelectors';
import {
  getLazyTeammatesInCommonTeam,
  makeGetTeammateListWithLazyValidation,
  TeammatesSelector,
} from '../teammates/lazyTeammatesSelectors';
import {getTeammatesInCommonTeam} from '../teammates/teammatesSelectors';
import {computeWorkspaceSortOrderMap} from './sort/workspaceSortOrderMapHelpers';

type BooleanSelector<P> = AppParametricSelector<P, boolean>;

export function getWorkspace(state: AppState) {
  return state.data.workspace;
}

/**
 * Uses workspace v2.
 * Tags (combination of private and team tags) for the personal workspace.
 */
export function getPersonalWorkspaceTags(state: AppState): ReadonlyArray<WorkspaceTagV2> {
  return state.data.workspace.value[WorkspaceSectionTypesEnum.MY_INBOX_V2].tags;
}

/**
 * Uses workspace v2.
 * Any private inboxes or labels nested with the inboxes (Discussions, Assigned to me, Shared with me).
 */
export function getPersonalWorkspaceNestedFolders(state: AppState) {
  return state.data.workspace.value[WorkspaceSectionTypesEnum.MY_INBOX_V2].nested_folders;
}

/**
 * Uses workspace v2.
 * Any labels outside of the Inbox section (Starred, Sent, etc.).
 */
export function getPersonalWorkspaceRootFolders(state: AppState) {
  return state.data.workspace.value[WorkspaceSectionTypesEnum.MY_INBOX_V2].root_folders;
}

/** Uses workspace v2. */
export function getWorkspaceTeams(state: AppState) {
  return state.data.workspace.value[WorkspaceSectionTypesEnum.TEAMS_V2];
}

/** Uses workspace v2 (lenses are not in V1). */
export function getWorkspaceLenses(state: AppState) {
  return state.data.workspace.value[WorkspaceSectionTypesEnum.LENSES];
}

/** Uses workspace V2. */
function getWorkspaceDelegations(state: AppState) {
  return state.data.workspace.value[WorkspaceSectionTypesEnum.TEAMMATE_DELEGATIONS_V2];
}

/** Find the filter to use in team context. */
export const getTeamContextFilter = createSelector(
  [getShouldUseOpenFilterForTeams],
  (shouldShowTeammateTabsInTeamContext) =>
    shouldShowTeammateTabsInTeamContext ? InboxesFiltersEnum.OPEN : InboxesFiltersEnum.UNASSIGNED,
);

/** Uses workspace v2. */
export function getWorkspaceAssignedTeammates(state: AppState) {
  return state.data.workspace.value[WorkspaceSectionTypesEnum.TEAMMATE_CONVERSATIONS_V2];
}

/**
 * Uses workspace V2.
 * @param getTeammatesList This selector assumes this selects a pre-alphabetized list and uses its ordering for any needed alphabetization.
 */
function makeGetWorkspaceTeammates<P>(
  getAlphabetizedTeammatesList: TeammatesSelector<P>,
  getShouldAlphabetize: BooleanSelector<P>,
): TeammatesSelector<P> {
  return createSelector(
    [
      getWorkspaceAssignedTeammates,
      getAlphabetizedTeammatesList,
      getDisplayedTeammateIds,
      getShouldAlphabetize,
    ],
    (workspaceTeammates, alphabetizedTeammates, displayedTeammateIds, shouldAlphabetize) => {
      const displayedTeammates = alphabetizedTeammates.filter((t) => displayedTeammateIds.includes(t.id));

      if (shouldAlphabetize) {
        return displayedTeammates;
      }
      return _.sortBy(displayedTeammates, (t) =>
        workspaceTeammates.findIndex((workspaceTeammate) => workspaceTeammate.id === t.id),
      );
    },
  );
}

/** Uses workspace V2. */
export function makeGetWorkspaceTeammatesWithLazyValidation<P>(
  getShouldAlphabetize: BooleanSelector<P>,
  tag: string,
) {
  return makeGetTeammateListWithLazyValidation<P>(
    (getTeammates) => makeGetWorkspaceTeammates(getTeammates, getShouldAlphabetize),
    getTeammatesInCommonTeam,
    getLazyTeammatesInCommonTeam,
    tag,
  );
}

/**
 * Uses workspace V2.
 * Sorted based upon teammate alphabetization preference.
 */
export const getWorkspaceTeammatesWithLazyValidationSorted = makeGetWorkspaceTeammatesWithLazyValidation(
  getShouldAlphabetizeWorkspace,
  'getWorkspaceTeammatesWithLazyValidationSorted',
);

/** Uses workspace V2. */
export function makeGetWorkspaceDelegationsSorted<P>(getShouldAlphabetize: BooleanSelector<P>) {
  return createSelector(
    [getWorkspaceDelegations, getVisibleDelegations, getShouldAlphabetize],
    (workspaceDelegations, visibleDelegations, shouldAlphabetize) => {
      if (shouldAlphabetize) {
        return visibleDelegations;
      }

      const sortOrderMap = computeWorkspaceSortOrderMap(workspaceDelegations, ({id}) => id);
      return _(visibleDelegations)
        .sortBy((delegation) => sortOrderMap[delegation.id])
        .value();
    },
  );
}
